// # Expense History Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../../auth/state';
import SharedHeader from '../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import { collection, getDocs, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import { 
  FaFilter, 
  FaSort,
  FaDownload,
  FaSearch,
  FaCalendarAlt,
  FaTags,
  FaDollarSign,
  FaExpand,
  FaTimes,
  FaChartLine,
  FaMoneyBillWave,
  FaChartPie,
  FaArrowLeft,
  FaWallet,
  FaEdit,
  FaTrash,
  FaExclamationTriangle,
  FaCheck,
  FaSave,
  FaHistory,
  FaClock,
  FaChartBar
} from 'react-icons/fa';
import './expense_history.css';

const ExpenseHistory = () => {
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  
  // # Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    category: '',
    minAmount: '',
    maxAmount: '',
    currency: ''
  });

  // # Search state
  const [searchQuery, setSearchQuery] = useState('');

  // # Sort state
  const [sortConfig, setSortConfig] = useState({
    key: 'timestamp',
    direction: 'desc'
  });

  // # Categories for filter
  const expenseCategories = [
    'software maintenance',
    'hosting',
    'subscription',
    'marketing',
    'content development',
    'investment',
    'budget leftover',
    'other'
  ];

  // # Currencies for filter
  const currencies = ['USD', 'EGP', 'EUR', 'GBP', 'SAR', 'AED', 'KWD', 'QAR', 'BHD', 'OMR'];

  // # Add new state for selected expense
  const [selectedExpense, setSelectedExpense] = useState(null);

  // # Add new state for details popup
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);

  // # Add filter dialog state
  const [showFilters, setShowFilters] = useState(false);

  // # Add edit mode state
  const [editMode, setEditMode] = useState(false);
  
  // # Add state for expense being edited
  const [editingExpense, setEditingExpense] = useState(null);
  
  // # Add state for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // # Add state for expense to delete
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  
  // # Add state for operation status
  const [operationStatus, setOperationStatus] = useState({
    type: '', // 'success', 'error'
    message: '',
    show: false
  });

  // # Fetch expenses from Firestore
  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        const financialRef = collection(db, 'financial');
        const querySnapshot = await getDocs(financialRef);
        
        let allExpenses = [];
        
        querySnapshot.forEach(doc => {
          // # Get document data (monthly financial records)
          const data = doc.data();
          
          // # Check if the document has expenses
          if (data.expenses) {
            // # Process each expense in the document
            Object.entries(data.expenses).forEach(([expenseId, expenseData]) => {
              // # Add expense ID and document ID to the expense data
              const processedExpense = {
                ...expenseData,
                expense_id: expenseId, // # Store the complete expense ID
                document_id: doc.id,
                // # Create timestamp from created_at for compatibility with existing code
                timestamp: expenseData.metadata?.created_at || new Date().toISOString()
              };
              
              allExpenses.push(processedExpense);
            });
          }
        });

        // # Sort expenses by timestamp (newest first)
        allExpenses.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        
        setExpenses(allExpenses);
        setFilteredExpenses(allExpenses);
      } catch (error) {
        console.error('Error fetching expenses:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isAdmin) {
      fetchExpenses();
    }
  }, [isAdmin]);

  // # Handle search and filters
  useEffect(() => {
    let result = [...expenses];

    // # Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(expense => 
        expense.details?.toLowerCase().includes(query) ||
        expense.category?.toLowerCase().includes(query) ||
        expense.register_admin?.email?.toLowerCase().includes(query)
      );
    }

    // # Apply filters
    if (filters.startDate) {
      result = result.filter(expense => 
        new Date(expense.timestamp) >= new Date(filters.startDate)
      );
    }

    if (filters.endDate) {
      result = result.filter(expense => 
        new Date(expense.timestamp) <= new Date(filters.endDate)
      );
    }

    if (filters.category) {
      result = result.filter(expense => 
        expense.category === filters.category
      );
    }

    if (filters.currency) {
      result = result.filter(expense => 
        expense.currency === filters.currency
      );
    }

    if (filters.minAmount) {
      result = result.filter(expense => 
        parseFloat(expense.amount_native_currency) >= parseFloat(filters.minAmount)
      );
    }

    if (filters.maxAmount) {
      result = result.filter(expense => 
        parseFloat(expense.amount_native_currency) <= parseFloat(filters.maxAmount)
      );
    }

    // # Apply sorting
    result.sort((a, b) => {
      if (sortConfig.key === 'timestamp') {
        return sortConfig.direction === 'asc' 
          ? new Date(a.timestamp) - new Date(b.timestamp)
          : new Date(b.timestamp) - new Date(a.timestamp);
      }
      
      if (sortConfig.key === 'amount') {
        return sortConfig.direction === 'asc'
          ? parseFloat(a.amount_native_currency) - parseFloat(b.amount_native_currency)
          : parseFloat(b.amount_native_currency) - parseFloat(a.amount_native_currency);
      }

      return 0;
    });

    setFilteredExpenses(result);
  }, [expenses, searchQuery, filters, sortConfig]);

  // # Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // # Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // # Export to CSV
  const exportToCSV = () => {
    const headers = ['Date', 'Category', 'Details', 'Amount', 'Currency', 'EGP Total', 'Admin'];
    const csvData = filteredExpenses.map(expense => [
      new Date(expense.timestamp).toLocaleDateString(),
      expense.category,
      expense.details || '',
      expense.amount_native_currency,
      expense.currency,
      expense.egp_total,
      expense.register_admin?.email
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `expense_history_${new Date().toISOString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // # Handle maximize click
  const handleMaximize = (expense) => {
    setSelectedExpense(expense);
  };

  // # Handle close report
  const handleCloseReport = () => {
    setSelectedExpense(null);
  };

  // # Handle read more click
  const handleReadMore = (e) => {
    e.stopPropagation();
    setShowDetailsPopup(true);
  };

  // # Handle close details popup
  const handleCloseDetails = (e) => {
    e.stopPropagation();
    setShowDetailsPopup(false);
  };

  // # Handle filter dialog
  const handleOpenFilters = () => setShowFilters(true);
  const handleCloseFilters = () => setShowFilters(false);

  // # Handle filter apply
  const handleApplyFilters = () => {
    handleCloseFilters();
  };

  // # Handle filter reset
  const handleResetFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      category: '',
      minAmount: '',
      maxAmount: '',
      currency: ''
    });
    handleCloseFilters();
  };

  // # Handle edit button click
  const handleEditClick = (expense) => {
    // # Set the expense to edit and enable edit mode
    setEditingExpense({
      ...expense,
      expense_id: expense.expense_id,
      document_id: expense.document_id,
      amount_native_currency: expense.amount_native_currency,
      egp_total: expense.egp_total,
      details: expense.details || '',
      category: expense.category
    });
    setEditMode(true);
    // # Close the report view if it's open
    setSelectedExpense(null);
  };

  // # Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditingExpense(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // # Handle save edit
  const handleSaveEdit = async () => {
    try {
      setLoading(true);
      
      // # Get the document reference
      const docRef = doc(db, 'financial', editingExpense.document_id);
      
      // # Get the current document data
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const docData = docSnap.data();
        const expenses = { ...docData.expenses };
        
        // # Use the complete expense ID from the editingExpense object
        const completeExpenseId = editingExpense.expense_id;
        
        if (expenses && completeExpenseId && expenses[completeExpenseId]) {
          // # Create updated expense data while preserving original metadata and admin info
          const updatedExpense = {
            ...expenses[completeExpenseId],
            category: editingExpense.category,
            details: editingExpense.details,
            amount_native_currency: Number(editingExpense.amount_native_currency),
            egp_total: Number(editingExpense.egp_total),
            metadata: {
              ...expenses[completeExpenseId].metadata,
              updated_at: new Date().toISOString()
            }
          };
          
          // # Update the expenses object
          expenses[completeExpenseId] = updatedExpense;
          
          // # Update the entire expenses object at once
          await updateDoc(docRef, {
            expenses: expenses
          });
          
          // # Update local state
          setExpenses(prev => 
            prev.map(exp => 
              exp.expense_id === completeExpenseId 
                ? { ...exp, ...updatedExpense }
                : exp
            )
          );
          
          // # Show success message
          setOperationStatus({
            type: 'success',
            message: 'Expense updated successfully',
            show: true
          });
          
          // # Hide success message after 3 seconds
          setTimeout(() => {
            setOperationStatus(prev => ({ ...prev, show: false }));
          }, 3000);
        } else {
          throw new Error('Expense not found in document');
        }
      }
    } catch (error) {
      console.error('Error updating expense:', error);
      
      // # Show error message
      setOperationStatus({
        type: 'error',
        message: 'Error updating expense: ' + error.message,
        show: true
      });
      
      // # Hide error message after 5 seconds
      setTimeout(() => {
        setOperationStatus(prev => ({ ...prev, show: false }));
      }, 5000);
    } finally {
      setLoading(false);
      setEditMode(false);
      setEditingExpense(null);
    }
  };

  // # Handle cancel edit
  const handleCancelEdit = () => {
    setEditMode(false);
    setEditingExpense(null);
  };

  // # Handle delete button click
  const handleDeleteClick = (expense) => {
    setExpenseToDelete(expense);
    setShowDeleteConfirm(true);
    // # Close the report view if it's open
    setSelectedExpense(null);
  };

  // # Handle confirm delete
  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      
      // # Get the document reference
      const docRef = doc(db, 'financial', expenseToDelete.document_id);
      
      // # Get the current document data
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const docData = docSnap.data();
        
        // # Check if the expense exists in the document
        if (docData.expenses && docData.expenses[expenseToDelete.expense_id]) {
          // # Create a new expenses object without the deleted expense
          const updatedExpenses = { ...docData.expenses };
          delete updatedExpenses[expenseToDelete.expense_id];
          
          // # Update the document
          await updateDoc(docRef, {
            expenses: updatedExpenses
          });
          
          // # Update local state
          setExpenses(prev => 
            prev.filter(exp => exp.expense_id !== expenseToDelete.expense_id)
          );
          
          // # Show success message
          setOperationStatus({
            type: 'success',
            message: 'Expense deleted successfully',
            show: true
          });
          
          // # Hide success message after 3 seconds
          setTimeout(() => {
            setOperationStatus(prev => ({ ...prev, show: false }));
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Error deleting expense:', error);
      
      // # Show error message
      setOperationStatus({
        type: 'error',
        message: 'Error deleting expense: ' + error.message,
        show: true
      });
      
      // # Hide error message after 5 seconds
      setTimeout(() => {
        setOperationStatus(prev => ({ ...prev, show: false }));
      }, 5000);
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setExpenseToDelete(null);
    }
  };

  // # Handle cancel delete
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setExpenseToDelete(null);
  };

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Calculate stats from filtered expenses
  const calculateStats = () => {
    const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + parseFloat(exp.egp_total), 0);
    const avgExpense = totalExpenses / (filteredExpenses.length || 1);
    const categoryCounts = filteredExpenses.reduce((acc, exp) => {
      acc[exp.category] = (acc[exp.category] || 0) + 1;
      return acc;
    }, {});
    const topCategory = Object.entries(categoryCounts)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 'N/A';

    return {
      total: totalExpenses.toFixed(2),
      count: filteredExpenses.length,
      average: avgExpense.toFixed(2),
      topCategory: topCategory.charAt(0).toUpperCase() + topCategory.slice(1),
      budgetSourced: true
    };
  };

  const stats = calculateStats();

  return (
    <div className="expn_hst_page">
      <SharedHeader />
      
      <main className="expn_hst_content">
        <div className="expn_hst_header">
          <h1>Expense History</h1>
          <div className="expn_hst_header-controls">
            <button 
              className="expn_hst_filter-button"
              onClick={handleOpenFilters}
            >
              <FaFilter /> Filters
            </button>
            <button 
              className="expn_hst_back-button"
              onClick={() => navigate('/admin/financial/expenses')}
            >
              <FaArrowLeft /> Back to Expenses
            </button>
          </div>
        </div>

        {/* Stats Dashboard */}
        <div className="expn_hst_stats-dashboard">
          <div className="expn_hst_stats-grid">
            <div className="expn_hst_stat-card">
              <FaMoneyBillWave className="expn_hst_stat-icon" />
              <div className="expn_hst_stat-value">E£ {stats.total}</div>
              <div className="expn_hst_stat-label">Total Expenses</div>
            </div>
            <div className="expn_hst_stat-card">
              <FaChartLine className="expn_hst_stat-icon" />
              <div className="expn_hst_stat-value">{stats.count}</div>
              <div className="expn_hst_stat-label">Total Transactions</div>
            </div>
            <div className="expn_hst_stat-card">
              <FaDollarSign className="expn_hst_stat-icon" />
              <div className="expn_hst_stat-value">E£ {stats.average}</div>
              <div className="expn_hst_stat-label">Average Expense</div>
            </div>
            <div className="expn_hst_stat-card">
              <FaChartPie className="expn_hst_stat-icon" />
              <div className="expn_hst_stat-value">{stats.topCategory}</div>
              <div className="expn_hst_stat-label">Top Category</div>
            </div>
            <div className="expn_hst_stat-card">
              <FaWallet className="expn_hst_stat-icon" />
              <div className="expn_hst_stat-value">Budget Account</div>
              <div className="expn_hst_stat-label">Expense Source</div>
            </div>
          </div>
        </div>

        {/* Search and Export */}
        <div className="expn_hst_controls">
          <div className="expn_hst_search">
            <FaSearch className="expn_hst_icon" />
            <input
              type="text"
              placeholder="Search expenses..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button 
            className="expn_hst_export-button"
            onClick={exportToCSV}
          >
            <FaDownload /> Export CSV
          </button>
        </div>

        {/* Expenses Table */}
        <div className="expn_hst_table-container">
          {loading ? (
            <div className="expn_hst_loading">Loading expenses...</div>
          ) : filteredExpenses.length === 0 ? (
            <div className="expn_hst_no-data">No expenses found</div>
          ) : (
            <table className="expn_hst_table">
              <thead>
                <tr>
                  <th></th>
                  <th onClick={() => handleSort('timestamp')}>
                    Date {sortConfig.key === 'timestamp' && (
                      <FaSort className={`expn_hst_sort-icon ${sortConfig.direction}`} />
                    )}
                  </th>
                  <th>Category</th>
                  <th onClick={() => handleSort('amount')}>
                    Amount {sortConfig.key === 'amount' && (
                      <FaSort className={`expn_hst_sort-icon ${sortConfig.direction}`} />
                    )}
                  </th>
                  <th>Currency</th>
                  <th>EGP Total</th>
                  <th>Admin</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses.map((expense, index) => (
                  <tr key={expense.expense_id || index}>
                    <td>
                      <button 
                        className="expn_hst_maximize-btn"
                        onClick={() => handleMaximize(expense)}
                        title="View Details"
                      >
                        <FaExpand />
                      </button>
                    </td>
                    <td>{new Date(expense.timestamp).toLocaleDateString()}</td>
                    <td>{expense.category}</td>
                    <td>{expense.amount_native_currency}</td>
                    <td>{expense.currency}</td>
                    <td>{expense.egp_total}</td>
                    <td>{expense.register_admin?.email}</td>
                    <td className="expn_hst_actions">
                      <button 
                        className="expn_hst_edit-btn"
                        onClick={() => handleEditClick(expense)}
                        title="Edit Expense"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="expn_hst_delete-btn"
                        onClick={() => handleDeleteClick(expense)}
                        title="Delete Expense"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Filters Dialog */}
        {showFilters && (
          <div className="expn_hst_dialog-overlay" onClick={handleCloseFilters}>
            <div className="expn_hst_dialog" onClick={e => e.stopPropagation()}>
              <div className="expn_hst_dialog-header">
                <h3>Filter Expenses</h3>
                <button 
                  className="expn_hst_dialog-close"
                  onClick={handleCloseFilters}
                >
                  <FaTimes />
                </button>
              </div>
              
              <div className="expn_hst_dialog-content">
                <div className="expn_hst_filter-section">
                  <h4>Date Range</h4>
                  <div className="expn_hst_filter-group">
                    <FaCalendarAlt className="expn_hst_icon" />
                    <input
                      type="date"
                      name="startDate"
                      value={filters.startDate}
                      onChange={handleFilterChange}
                      placeholder="Start Date"
                    />
                    <input
                      type="date"
                      name="endDate"
                      value={filters.endDate}
                      onChange={handleFilterChange}
                      placeholder="End Date"
                    />
                  </div>
                </div>

                <div className="expn_hst_filter-section">
                  <h4>Category</h4>
                  <div className="expn_hst_filter-group">
                    <FaTags className="expn_hst_icon" />
                    <select
                      name="category"
                      value={filters.category}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Categories</option>
                      {expenseCategories.map((category, index) => (
                        <option key={index} value={category}>
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="expn_hst_filter-section">
                  <h4>Currency</h4>
                  <div className="expn_hst_filter-group">
                    <FaDollarSign className="expn_hst_icon" />
                    <select
                      name="currency"
                      value={filters.currency}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Currencies</option>
                      {currencies.map((currency, index) => (
                        <option key={index} value={currency}>
                          {currency}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="expn_hst_filter-section">
                  <h4>Amount Range</h4>
                  <div className="expn_hst_filter-group">
                    <input
                      type="number"
                      name="minAmount"
                      value={filters.minAmount}
                      onChange={handleFilterChange}
                      placeholder="Min Amount"
                    />
                    <input
                      type="number"
                      name="maxAmount"
                      value={filters.maxAmount}
                      onChange={handleFilterChange}
                      placeholder="Max Amount"
                    />
                  </div>
                </div>
              </div>

              <div className="expn_hst_dialog-footer">
                <button 
                  className="expn_hst_dialog-button secondary"
                  onClick={handleResetFilters}
                >
                  Reset
                </button>
                <button 
                  className="expn_hst_dialog-button primary"
                  onClick={handleApplyFilters}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expense Report Modal */}
        {selectedExpense && (
          <div className="expn_hst_modal-overlay">
            <div className="expn_hst_modal">
              <div className="expn_hst_modal-header">
                <h2>Expense Report</h2>
                <button 
                  className="expn_hst_modal-close"
                  onClick={handleCloseReport}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="expn_hst_modal-content">
                <div className="expn_hst_report-grid">
                  <div className="expn_hst_report-item">
                    <label>Expense ID</label>
                    <span>{selectedExpense.expense_id}</span>
                  </div>
                  <div className="expn_hst_report-item">
                    <label>Date</label>
                    <span>{new Date(selectedExpense.timestamp).toLocaleString()}</span>
                  </div>
                  <div className="expn_hst_report-item">
                    <label>Category</label>
                    <span>{selectedExpense.category}</span>
                  </div>
                  <div className="expn_hst_report-item">
                    <label>Amount</label>
                    <span>{selectedExpense.currency_symbol} {selectedExpense.amount_native_currency}</span>
                  </div>
                  <div className="expn_hst_report-item">
                    <label>EGP Total</label>
                    <span>E£ {selectedExpense.egp_total}</span>
                  </div>
                  <div className="expn_hst_report-item">
                    <div className="expn_hst_details-header">
                      <label>Details</label>
                      {selectedExpense.details && (
                        <button 
                          className="expn_hst_read-more"
                          onClick={handleReadMore}
                        >
                          Read More
                        </button>
                      )}
                    </div>
                    <span>
                      {selectedExpense.details 
                        ? (selectedExpense.details.length > 100 
                          ? selectedExpense.details.substring(0, 100) + '...' 
                          : selectedExpense.details)
                        : 'No details provided'
                      }
                    </span>
                  </div>
                  <div className="expn_hst_report-item">
                    <label>Admin</label>
                    <span>{selectedExpense.register_admin?.name || selectedExpense.register_admin?.email}</span>
                  </div>
                  <div className="expn_hst_report-item">
                    <label>Transaction Type</label>
                    <span>{selectedExpense.transaction_type || 'expense'}</span>
                  </div>
                  <div className="expn_hst_report-item">
                    <label>Created At</label>
                    <span>{new Date(selectedExpense.metadata?.created_at).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Details Popup */}
        {showDetailsPopup && selectedExpense && (
          <div className="expn_hst_details-popup-overlay" onClick={handleCloseDetails}>
            <div className="expn_hst_details-popup" onClick={e => e.stopPropagation()}>
              <div className="expn_hst_details-popup-header">
                <h3>Expense Details</h3>
                <button 
                  className="expn_hst_details-popup-close"
                  onClick={handleCloseDetails}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="expn_hst_details-popup-content">
                {selectedExpense.details}
              </div>
            </div>
          </div>
        )}

        {/* Edit Expense Form */}
        {editMode && editingExpense && (
          <div className="expn_hst_modal-overlay">
            <div className="expn_hst_modal expn_hst_edit-modal">
              <div className="expn_hst_modal-header">
                <h2>Edit Expense</h2>
                <button 
                  className="expn_hst_modal-close"
                  onClick={handleCancelEdit}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="expn_hst_modal-content">
                <form className="expn_hst_edit-form">
                  <div className="expn_hst_form-group">
                    <label>Category</label>
                    <select
                      name="category"
                      value={editingExpense.category}
                      onChange={handleEditFormChange}
                      required
                    >
                      {expenseCategories.map((category, index) => (
                        <option key={index} value={category}>
                          {category.charAt(0).toUpperCase() + category.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="expn_hst_form-group">
                    <label>Amount ({editingExpense.currency})</label>
                    <input
                      type="number"
                      name="amount_native_currency"
                      value={editingExpense.amount_native_currency}
                      onChange={handleEditFormChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="expn_hst_form-group">
                    <label>EGP Total</label>
                    <input
                      type="number"
                      name="egp_total"
                      value={editingExpense.egp_total}
                      onChange={handleEditFormChange}
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="expn_hst_form-group">
                    <label>Details</label>
                    <textarea
                      name="details"
                      value={editingExpense.details}
                      onChange={handleEditFormChange}
                      rows="4"
                    />
                  </div>
                  
                  <div className="expn_hst_form-actions">
                    <button 
                      type="button" 
                      className="expn_hst_cancel-btn"
                      onClick={handleCancelEdit}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button" 
                      className="expn_hst_save-btn"
                      onClick={handleSaveEdit}
                    >
                      <FaSave /> Save Changes
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && expenseToDelete && (
          <div className="expn_hst_modal-overlay">
            <div className="expn_hst_modal expn_hst_delete-modal">
              <div className="expn_hst_modal-header">
                <h2>Confirm Delete</h2>
                <button 
                  className="expn_hst_modal-close"
                  onClick={handleCancelDelete}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="expn_hst_modal-content">
                <div className="expn_hst_delete-warning">
                  <FaExclamationTriangle className="expn_hst_warning-icon" />
                  <p>Are you sure you want to delete this expense?</p>
                  <p className="expn_hst_delete-details">
                    <strong>Category:</strong> {expenseToDelete.category}<br />
                    <strong>Amount:</strong> {expenseToDelete.currency_symbol} {expenseToDelete.amount_native_currency}<br />
                    <strong>Date:</strong> {new Date(expenseToDelete.timestamp).toLocaleDateString()}
                  </p>
                  <p className="expn_hst_delete-permanent">This action cannot be undone.</p>
                </div>
                <div className="expn_hst_delete-actions">
                  <button 
                    className="expn_hst_cancel-btn"
                    onClick={handleCancelDelete}
                  >
                    Cancel
                  </button>
                  <button 
                    className="expn_hst_delete-confirm-btn"
                    onClick={handleConfirmDelete}
                  >
                    <FaTrash /> Delete Expense
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Operation Status Toast */}
        {operationStatus.show && (
          <div className={`expn_hst_toast ${operationStatus.type}`}>
            {operationStatus.type === 'success' ? (
              <FaCheck className="expn_hst_toast-icon" />
            ) : (
              <FaExclamationTriangle className="expn_hst_toast-icon" />
            )}
            <span>{operationStatus.message}</span>
          </div>
        )}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default ExpenseHistory;
